import {createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {authMeKey, fetchAuthMe, useAuthMe} from "../../api/auth/authQueries";
import {hasLocalLoginToken, setLogin, setLogout as clearLogin} from "../../util/loginStatus";
import {UserRole} from "../../util/userRole";

type AuthStatus = "checking" | "authed" | "unauth";

type AuthContextType = {
    status: AuthStatus;
    isAuthenticated: boolean;
    isAdmin: boolean;
    username: string | null;
    role: UserRole | null;
    completeLogin: (secondsUntilLogout: number) => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
    status: "checking",
    isAuthenticated: false,
    isAdmin: false,
    username: null,
    role: null,
    completeLogin: async () => undefined,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({children}: {children: ReactNode}) => {
    const queryClient = useQueryClient();
    const [hasSession, setHasSession] = useState(hasLocalLoginToken());
    const authMeQuery = useAuthMe(hasSession);

    useEffect(() => {
        if (!hasSession || !authMeQuery.isError) {
            return;
        }

        clearLogin();
        setHasSession(false);
        queryClient.removeQueries({queryKey: authMeKey});
    }, [authMeQuery.isError, hasSession, queryClient]);

    const completeLogin = useCallback(
        async (secondsUntilLogout: number) => {
            setLogin(secondsUntilLogout);
            setHasSession(true);
            try {
                await queryClient.fetchQuery({
                    queryKey: authMeKey,
                    queryFn: fetchAuthMe,
                });
            } catch (error) {
                clearLogin();
                setHasSession(false);
                queryClient.removeQueries({queryKey: authMeKey});
                throw error;
            }
        },
        [queryClient],
    );

    const value = useMemo<AuthContextType>(() => {
        const unauthenticatedValue = {
            isAuthenticated: false,
            isAdmin: false,
            username: null,
            role: null,
            completeLogin,
        } satisfies Omit<AuthContextType, "status">;

        if (!hasSession) {
            return {
                status: "unauth",
                ...unauthenticatedValue,
            };
        }

        if (authMeQuery.isPending) {
            return {
                status: "checking",
                ...unauthenticatedValue,
            };
        }

        if (authMeQuery.isError || !authMeQuery.data) {
            return {
                status: "unauth",
                ...unauthenticatedValue,
            };
        }

        return {
            status: "authed",
            isAuthenticated: true,
            isAdmin: authMeQuery.data.role === UserRole.ADMIN,
            username: authMeQuery.data.username,
            role: authMeQuery.data.role,
            completeLogin,
        };
    }, [authMeQuery.data, authMeQuery.isError, authMeQuery.isPending, completeLogin, hasSession]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
